import Dexie, { Table } from "dexie";
import { newUploadId } from "@uploads";

/**
 * LSP-19313: durable local copy of a Spark-guide document Save that is mid-upload.
 *
 * `uploadToPresignedUrl` (components/scribe/api.ts) already survives a single transient blip via
 * an in-process 3-attempt retry (F-16). That retry lives entirely in memory: if all attempts fail
 * (a longer outage, a captive-portal wifi drop, a laptop sleeping) or the tab reloads while a retry
 * is in flight, the chosen bytes and the retained document stub id are gone and the user's edit has
 * to be redone from scratch. This store persists exactly enough to resume — the bytes, the stub id,
 * and the target requirement — so `SparkGuideEditor.vue` can offer the file back on the next load
 * instead of silently discarding it.
 *
 * Deliberately NOT the AdminFileUploads chunked/multipart pattern (fileUploadDb.ts): this is a
 * single small PUT with a single retained document stub, so it needs none of that store's
 * multi-tab ownership/lease/heartbeat machinery. One row per authenticated owner and requirement
 * is enough — reusing the STORAGE pattern (durable IndexedDB bytes), not the tray/ownership logic,
 * per the ticket's suggested direction. The pre-v6 requirement-only table is retained as a
 * quarantine: those rows have no trustworthy owner and are never presented or deleted automatically.
 */
export interface ScribePendingUploadOwner {
	organizationId: string;
	userId: string;
}

export interface ScribePendingDocumentUpload extends ScribePendingUploadOwner {
	/** Compound primary key. One in-flight upload per authenticated owner and requirement. */
	requirementId: string;
	/** Identifies the exact Save attempt that wrote this requirement-scoped row. */
	attemptId: string;
	/** The retained Document stub id (F-16 (b)) to reuse on resume, or null if none was minted yet. */
	pendingDocumentId: string | null;
	/** Whether the bytes came from an editable text save or a staged binary file. */
	kind: "text" | "binary";
	fileName: string;
	mimeType: string;
	/**
	 * Stored as ArrayBuffer, not Blob. `AdminSmallFileUpload` (fileUploadDb.ts) documents that some
	 * IndexedDB implementations (WebKit; also observed under fake-indexeddb here) only structured-clone
	 * a Blob's metadata, not its bytes — that's why it carries a `payloadBytes` fallback. This store has
	 * no owner-scoped legacy rows to stay compatible with, so it goes straight to the durable ArrayBuffer form.
	 */
	payloadBytes: ArrayBuffer;
	createdAt: number;
	/** Cross-context causal order. Absent when sequence issuance failed or on a pre-v5 row. */
	sequence?: number;
	/** Confirmed uploads must never be offered as interrupted work, even if deletion failed. */
	status: "pending" | "confirmed";
}

type ScribePendingDocumentUploadKey = [organizationId: string, userId: string, requirementId: string];
type ScribeLegacyPendingDocumentUpload = Omit<ScribePendingDocumentUpload, keyof ScribePendingUploadOwner>;

class ScribePendingUploadDatabase extends Dexie {
	/** Pre-v6 unowned rows are retained here but never presented or mutated by recovery. */
	pendingDocumentUploads!: Table<ScribeLegacyPendingDocumentUpload, string>;
	ownerPendingDocumentUploads!: Table<ScribePendingDocumentUpload, ScribePendingDocumentUploadKey>;
	confirmedDocumentUploadAttempts!: Table<ScribeConfirmedDocumentUploadAttempt, string>;
	sequenceCounters!: Table<ScribeSequenceCounter, string>;

	constructor() {
		super("ScribePendingUploads");
		this.version(1).stores({
			pendingDocumentUploads: "requirementId, createdAt"
		});
		this.version(2)
			.stores({
				pendingDocumentUploads: "requirementId, createdAt"
			})
			.upgrade((transaction) =>
				transaction
					.table<ScribeLegacyPendingDocumentUpload, string>("pendingDocumentUploads")
					.toCollection()
					.modify((upload) => {
						upload.status = "pending";
					})
			);
		this.version(3)
			.stores({
				pendingDocumentUploads: "requirementId, createdAt",
				confirmedDocumentUploadAttempts: "attemptId, confirmedAt"
			})
			.upgrade((transaction) =>
				transaction
					.table<ScribeLegacyPendingDocumentUpload, string>("pendingDocumentUploads")
					.toCollection()
					.modify((upload) => {
						upload.attemptId = newUploadId();
					})
			);
		this.version(4)
			.stores({
				pendingDocumentUploads: "requirementId, createdAt",
				confirmedDocumentUploadAttempts: "attemptId, documentId, confirmedAt"
			})
			.upgrade(async (transaction) => {
				const pendingUploads = await transaction
					.table<ScribeLegacyPendingDocumentUpload, string>("pendingDocumentUploads")
					.toArray();
				const documentIdByAttemptId = new Map(
					pendingUploads.map((upload) => [upload.attemptId, upload.pendingDocumentId] as const)
				);
				await transaction
					.table<{ attemptId: string; documentId?: string | null; confirmedAt: number }, string>(
						"confirmedDocumentUploadAttempts"
					)
					.toCollection()
					.modify((confirmation) => {
						confirmation.documentId = documentIdByAttemptId.get(confirmation.attemptId) ?? null;
					});
			});
		this.version(5)
			.stores({
				pendingDocumentUploads: "requirementId, createdAt",
				confirmedDocumentUploadAttempts: "attemptId, documentId, confirmedAt",
				sequenceCounters: "id"
			})
			.upgrade(async (transaction) => {
				const sequenceCounters = transaction.table<ScribeSequenceCounter, string>("sequenceCounters");
				if (!(await sequenceCounters.get(SCRIBE_GLOBAL_SEQUENCE_COUNTER_ID))) {
					await sequenceCounters.put({ id: SCRIBE_GLOBAL_SEQUENCE_COUNTER_ID, value: 0 });
				}
			});
		this.version(6).stores({
			pendingDocumentUploads: "requirementId, createdAt",
			ownerPendingDocumentUploads: "[organizationId+userId+requirementId], createdAt",
			confirmedDocumentUploadAttempts: "attemptId, documentId, confirmedAt",
			sequenceCounters: "id"
		});
	}
}

const SCRIBE_GLOBAL_SEQUENCE_COUNTER_ID = "global";
const db = new ScribePendingUploadDatabase();
const confirmedAttemptIds = new Set<string>();
const mostRecentConfirmationSequenceByDocumentId = new Map<string, number>();

interface ScribeSequenceCounter {
	id: string;
	value: number;
}

interface ScribeConfirmedDocumentUploadAttempt {
	attemptId: string;
	documentId: string | null;
	confirmedAt: number;
	/** Absent on pre-v5 rows or when sequence issuance failed. */
	sequence?: number;
}

export interface ScribePendingDocumentUploadInput extends ScribePendingUploadOwner {
	requirementId: string;
	pendingDocumentId: string | null;
	kind: "text" | "binary";
	fileName: string;
	mimeType: string;
	payload: Blob;
	createdAt: number;
}

export interface ScribePendingDocumentUploadSaveResult {
	attemptId: string;
	persisted: boolean;
	/**
	 * The row this save was going to supersede. `undefined` means its identity could not be read;
	 * `null` means the read succeeded and no prior row existed.
	 */
	supersededAttemptId?: string | null;
}

export type ScribePendingDocumentUploadReadResult =
	| { succeeded: true; upload: ScribePendingDocumentUpload | undefined }
	| { succeeded: false };

export type ScribeDocumentUploadAttemptConfirmation = "confirmed" | "not-confirmed" | "unknown";

function isValidOwnerId(value: unknown): value is string {
	return typeof value === "string" && value.trim().length > 0;
}

function pendingUploadKey(requirementId: string, owner: ScribePendingUploadOwner): ScribePendingDocumentUploadKey {
	if (!isValidOwnerId(owner.organizationId) || !isValidOwnerId(owner.userId)) {
		throw new Error("Cannot access a Scribe pending upload without a valid organization and user owner");
	}
	return [owner.organizationId, owner.userId, requirementId];
}

export function createScribePendingUploadOwner(
	organizationId: string | null | undefined,
	userId: string | null | undefined
): ScribePendingUploadOwner | null {
	if (!isValidOwnerId(organizationId) || !isValidOwnerId(userId)) {
		console.warn("[Scribe] Durable pending-upload owner is unavailable; preserving stored drafts without presenting or mutating them", {
			hasOrganizationId: Boolean(organizationId),
			hasUserId: Boolean(userId)
		});
		return null;
	}
	return { organizationId, userId };
}

async function nextScribeSequenceNumber(): Promise<number | undefined> {
	try {
		return await db.transaction("rw", db.sequenceCounters, async () => {
			const currentCounter = await db.sequenceCounters.get(SCRIBE_GLOBAL_SEQUENCE_COUNTER_ID);
			const nextSequence = (currentCounter?.value ?? 0) + 1;
			await db.sequenceCounters.put({ id: SCRIBE_GLOBAL_SEQUENCE_COUNTER_ID, value: nextSequence });
			return nextSequence;
		});
	} catch (error) {
		console.warn("[Scribe] Could not issue the next durable upload sequence number", error);
		return undefined;
	}
}

/**
 * Persist the bytes about to be PUT so a reload during the retry window can resume them.
 * Best-effort: a quota/IDB failure here must not block the upload itself, but IS a silent-failure
 * risk for the NEXT reload, so it is logged loudly (CLAUDE.md → "Diagnostic logging when fixing
 * silent failures").
 */
export async function saveScribePendingDocumentUpload(
	entry: ScribePendingDocumentUploadInput
): Promise<ScribePendingDocumentUploadSaveResult> {
	const attemptId = newUploadId();
	let supersededAttemptId: string | null | undefined;
	let key: ScribePendingDocumentUploadKey;
	try {
		key = pendingUploadKey(entry.requirementId, entry);
	} catch (error) {
		console.warn(`[Scribe] Refusing to persist an unowned pending document upload for requirement ${entry.requirementId}`, error);
		return { attemptId, persisted: false, supersededAttemptId };
	}
	try {
		supersededAttemptId = (await db.ownerPendingDocumentUploads.get(key))?.attemptId ?? null;
	} catch (error) {
		console.warn(
			`[Scribe] Could not identify the pending document upload being superseded for requirement ${entry.requirementId}`,
			error
		);
	}
	try {
		const payloadBytes = await entry.payload.arrayBuffer();
		const sequence = await nextScribeSequenceNumber();
		await db.ownerPendingDocumentUploads.put({
			organizationId: entry.organizationId,
			userId: entry.userId,
			requirementId: entry.requirementId,
			attemptId,
			pendingDocumentId: entry.pendingDocumentId,
			kind: entry.kind,
			fileName: entry.fileName,
			mimeType: entry.mimeType,
			payloadBytes,
			createdAt: entry.createdAt,
			sequence,
			status: "pending"
		});
		return { attemptId, persisted: true };
	} catch (error) {
		console.warn(
			`[Scribe] Could not durably persist pending document upload for requirement ${entry.requirementId} — ` +
				"a reload during this upload attempt will lose the file instead of resuming it",
			error
		);
		return { attemptId, persisted: false, supersededAttemptId };
	}
}

export async function tryReadScribePendingDocumentUpload(
	requirementId: string,
	owner: ScribePendingUploadOwner
): Promise<ScribePendingDocumentUploadReadResult> {
	try {
		return { succeeded: true, upload: await db.ownerPendingDocumentUploads.get(pendingUploadKey(requirementId, owner)) };
	} catch (error) {
		console.warn(`[Scribe] Could not read pending document upload for requirement ${requirementId}`, error);
		return { succeeded: false };
	}
}

export async function readScribePendingDocumentUpload(
	requirementId: string,
	owner: ScribePendingUploadOwner
): Promise<ScribePendingDocumentUpload | undefined> {
	const result = await tryReadScribePendingDocumentUpload(requirementId, owner);
	return result.succeeded ? result.upload : undefined;
}

/**
 * Establish a destructive operation's target only when consecutive reads agree. If an earlier
 * read failed, a later row cannot be attributed to the operation's start and must not be adopted.
 * A later absence is safe to accept because it requires no destructive action.
 */
export async function tryReadStableScribePendingDocumentUpload(
	requirementId: string,
	owner: ScribePendingUploadOwner
): Promise<ScribePendingDocumentUploadReadResult> {
	let candidateUpload: ScribePendingDocumentUpload | undefined;
	let hasCandidate = false;
	let earlierReadFailed = false;
	for (let readAttempt = 0; readAttempt < 2; readAttempt += 1) {
		const readResult = await tryReadScribePendingDocumentUpload(requirementId, owner);
		if (!readResult.succeeded) {
			earlierReadFailed = true;
			continue;
		}
		if (earlierReadFailed) {
			return readResult.upload ? { succeeded: false } : readResult;
		}
		if (!hasCandidate) {
			candidateUpload = readResult.upload;
			hasCandidate = true;
			continue;
		}
		if (candidateUpload?.attemptId === readResult.upload?.attemptId) return readResult;
		return { succeeded: false };
	}
	return { succeeded: false };
}

export async function clearScribePendingDocumentUpload(
	requirementId: string,
	owner: ScribePendingUploadOwner,
	attemptId?: string
): Promise<void> {
	try {
		const key = pendingUploadKey(requirementId, owner);
		if (!attemptId) {
			await db.ownerPendingDocumentUploads.delete(key);
			return;
		}
		await db.transaction("rw", db.ownerPendingDocumentUploads, async () => {
			const upload = await db.ownerPendingDocumentUploads.get(key);
			if (upload?.attemptId === attemptId) await db.ownerPendingDocumentUploads.delete(key);
		});
	} catch (error) {
		console.warn(`[Scribe] Could not clear pending document upload for requirement ${requirementId}`, error);
	}
}

export async function tryClearScribePendingDocumentUpload(
	requirementId: string,
	owner: ScribePendingUploadOwner,
	attemptId?: string
): Promise<boolean> {
	try {
		const key = pendingUploadKey(requirementId, owner);
		if (!attemptId) {
			await db.ownerPendingDocumentUploads.delete(key);
			return true;
		}
		await db.transaction("rw", db.ownerPendingDocumentUploads, async () => {
			const upload = await db.ownerPendingDocumentUploads.get(key);
			if (upload?.attemptId === attemptId) await db.ownerPendingDocumentUploads.delete(key);
		});
		return true;
	} catch (error) {
		console.warn(`[Scribe] Could not clear pending document upload for requirement ${requirementId}`, error);
		return false;
	}
}

export type ScribePendingDocumentUploadAttemptClearResult = "cleared" | "different-attempt" | "failed";

export async function tryClearScribePendingDocumentUploadAttempt(
	requirementId: string,
	owner: ScribePendingUploadOwner,
	attemptId: string
): Promise<ScribePendingDocumentUploadAttemptClearResult> {
	try {
		const key = pendingUploadKey(requirementId, owner);
		return await db.transaction("rw", db.ownerPendingDocumentUploads, async () => {
			const upload = await db.ownerPendingDocumentUploads.get(key);
			if (!upload) return "cleared";
			if (upload.attemptId !== attemptId) return "different-attempt";
			await db.ownerPendingDocumentUploads.delete(key);
			return "cleared";
		});
	} catch (error) {
		console.warn(`[Scribe] Could not clear pending document upload attempt ${attemptId} for requirement ${requirementId}`, error);
		return "failed";
	}
}

/**
 * Abandon the upload that is current when this operation starts without deleting a replacement
 * written by another tab. Reads are retried so a transient failure does not force callers to fall
 * back to an unsafe requirement-scoped delete.
 */
export async function tryAbandonScribePendingDocumentUpload(requirementId: string, owner: ScribePendingUploadOwner): Promise<boolean> {
	const targetReadResult = await tryReadStableScribePendingDocumentUpload(requirementId, owner);
	if (!targetReadResult.succeeded) {
		console.warn(`[Scribe] Could not establish which pending document upload to abandon for requirement ${requirementId}`);
		return false;
	}
	const targetUpload = targetReadResult.upload;
	if (!targetUpload) return true;

	for (let clearAttempt = 0; clearAttempt < 2; clearAttempt += 1) {
		const clearResult = await tryClearScribePendingDocumentUploadAttempt(requirementId, owner, targetUpload.attemptId);
		if (clearResult === "cleared") return true;
		if (clearResult === "different-attempt") {
			console.warn(`[Scribe] A newer pending document upload replaced the attempt being abandoned for requirement ${requirementId}`, {
				abandonedAttemptId: targetUpload.attemptId
			});
			return false;
		}
	}
	return false;
}

export async function markScribePendingDocumentUploadConfirmed(
	requirementId: string,
	owner: ScribePendingUploadOwner,
	attemptId: string
): Promise<boolean> {
	try {
		const key = pendingUploadKey(requirementId, owner);
		await db.transaction("rw", db.ownerPendingDocumentUploads, async () => {
			const upload = await db.ownerPendingDocumentUploads.get(key);
			if (upload?.attemptId === attemptId) await db.ownerPendingDocumentUploads.update(key, { status: "confirmed" });
		});
		return true;
	} catch (error) {
		console.warn(`[Scribe] Could not mark pending document upload confirmed for requirement ${requirementId}`, error);
		return false;
	}
}

/**
 * Record server confirmation independently of cleanup of the payload row. This is the durable
 * recovery fence for the compound case where both deleting and terminally updating that row fail.
 */
export async function recordScribeDocumentUploadConfirmed(attemptId: string, documentId: string): Promise<boolean> {
	const confirmedAt = Date.now();
	const sequence = await nextScribeSequenceNumber();
	confirmedAttemptIds.add(attemptId);
	const previousConfirmationSequence = mostRecentConfirmationSequenceByDocumentId.get(documentId);
	if (sequence !== undefined && (previousConfirmationSequence === undefined || sequence > previousConfirmationSequence)) {
		mostRecentConfirmationSequenceByDocumentId.set(documentId, sequence);
	}
	try {
		await db.confirmedDocumentUploadAttempts.put({ attemptId, documentId, confirmedAt, sequence });
		return true;
	} catch (error) {
		console.warn(`[Scribe] Could not record confirmed document upload attempt ${attemptId}`, error);
		return false;
	}
}

export async function isScribeDocumentUploadAttemptConfirmed(attemptId: string): Promise<ScribeDocumentUploadAttemptConfirmation> {
	if (confirmedAttemptIds.has(attemptId)) return "confirmed";
	try {
		const confirmed = Boolean(await db.confirmedDocumentUploadAttempts.get(attemptId));
		if (confirmed) confirmedAttemptIds.add(attemptId);
		return confirmed ? "confirmed" : "not-confirmed";
	} catch (error) {
		console.warn(`[Scribe] Could not check confirmed document upload attempt ${attemptId}`, error);
		return "unknown";
	}
}

export async function isScribeDocumentUploadConfirmed(
	documentId: string,
	candidateSequence: number | undefined
): Promise<ScribeDocumentUploadAttemptConfirmation> {
	if (candidateSequence === undefined) return "unknown";
	let mostRecentConfirmationSequence = mostRecentConfirmationSequenceByDocumentId.get(documentId);
	if (mostRecentConfirmationSequence !== undefined && mostRecentConfirmationSequence >= candidateSequence) return "confirmed";
	try {
		const confirmations = await db.confirmedDocumentUploadAttempts.where("documentId").equals(documentId).toArray();
		let hasConfirmationWithoutSequence = false;
		for (const confirmation of confirmations) {
			if (confirmation.sequence === undefined) {
				hasConfirmationWithoutSequence = true;
				continue;
			}
			if (mostRecentConfirmationSequence === undefined || confirmation.sequence > mostRecentConfirmationSequence) {
				mostRecentConfirmationSequence = confirmation.sequence;
			}
		}
		if (mostRecentConfirmationSequence !== undefined) {
			mostRecentConfirmationSequenceByDocumentId.set(documentId, mostRecentConfirmationSequence);
			if (mostRecentConfirmationSequence >= candidateSequence) return "confirmed";
		}
		return hasConfirmationWithoutSequence ? "unknown" : "not-confirmed";
	} catch (error) {
		console.warn(`[Scribe] Could not check confirmed document upload ${documentId}`, error);
		return "unknown";
	}
}
